import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Save, Star, ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { studioHeaders } from "@/lib/studioAuth";
import { cn } from "@/lib/utils";
import TravelZonesEditor from "@/pages/studio/TravelZonesEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankPackage = () => ({
    id: crypto.randomUUID(),
    kind: "shoot",
    name_en: "New package",
    name_cn: "新套餐",
    price: "0",
    unit_en: "/ shoot",
    unit_cn: "/ 套",
    description_en: "",
    description_cn: "",
    features_en: [],
    features_cn: [],
    early_bird_price: "",
    early_bird_note_en: "",
    early_bird_note_cn: "",
    popular: false,
    sort_order: 99,
    active: true,
    gumroad_url: "",
    section: "social",
    booking_mode: "deposit",
    price_prefix: "",
    travel_included_kv: false,
});

// Pricing-v2 scope fields — shown on every shoot package so the public card is never a vague name + price.
const SCOPE_TEXT = [
    ["scope_note", "Note under price", "备注(价格下方)"],
    ["duration", "Production duration", "拍摄时长"],
    ["locations", "Locations", "地点"],
    ["revisions", "Revisions", "修改"],
    ["travel", "Travel conditions", "旅程条件"],
    ["custom_quote", "Custom quotation conditions", "定制报价条件"],
    ["callout", "Callout (bold line)", "重点提示"],
];
const SCOPE_LISTS = [
    ["excludes", "Not included", "不包含"],
    ["quote_depends", "Final quotation depends on", "最终报价取决于"],
    ["conditions", "Pricing conditions", "价格条件"],
];

function ScopeEditor({ pkg, update }) {
    return (
        <div className="mt-6 pt-6 border-t border-border" data-testid={`pkg-scope-${pkg.id}`}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-4">Scope shown on the pricing card</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                <div>
                    <Label className="text-xs text-cafe-mocha">Section</Label>
                    <select value={pkg.section || "social"} onChange={e => update("section", e.target.value)} data-testid={`pkg-section-${pkg.id}`}
                        className="mt-1 w-full bg-cafe-cream border border-border rounded-xl px-3 h-10 text-sm">
                        <option value="social">Social Content Packages</option>
                        <option value="campaign">Brand Campaign Packages</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs text-cafe-mocha">Booking mode</Label>
                    <select value={pkg.booking_mode || "deposit"} onChange={e => update("booking_mode", e.target.value)} data-testid={`pkg-booking-mode-${pkg.id}`}
                        className="mt-1 w-full bg-cafe-cream border border-border rounded-xl px-3 h-10 text-sm">
                        <option value="deposit">Deposit · pay 50% online</option>
                        <option value="quote">Quotation · request before booking</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs text-cafe-mocha">Price prefix</Label>
                    <select value={pkg.price_prefix || ""} onChange={e => update("price_prefix", e.target.value)} data-testid={`pkg-price-prefix-${pkg.id}`}
                        className="mt-1 w-full bg-cafe-cream border border-border rounded-xl px-3 h-10 text-sm">
                        <option value="">Fixed (RM …)</option>
                        <option value="From">From RM …</option>
                    </select>
                </div>
                <div className="flex items-center gap-3 mt-6">
                    <Switch checked={!!pkg.travel_included_kv} onCheckedChange={v => update("travel_included_kv", v)} data-testid={`pkg-travel-kv-${pkg.id}`} />
                    <Label className="text-xs text-cafe-mocha cursor-pointer">Klang Valley travel included</Label>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {["en", "cn"].map(l => (
                    <div key={l} className="space-y-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">{l === "en" ? "English" : "中文"}</p>
                        {SCOPE_TEXT.map(([k, en, cn]) => (
                            <div key={k}>
                                <Label className="text-xs text-cafe-mocha">{l === "en" ? en : cn}</Label>
                                <Textarea rows={2} value={pkg[`${k}_${l}`] || ""} onChange={e => update(`${k}_${l}`, e.target.value)}
                                    className="mt-1 bg-cafe-cream border-border rounded-xl text-sm" />
                            </div>
                        ))}
                        {SCOPE_LISTS.map(([k, en, cn]) => (
                            <div key={k}>
                                <Label className="text-xs text-cafe-mocha">{l === "en" ? en : cn}</Label>
                                <div className="mt-2"><FeaturesList value={pkg[`${k}_${l}`]} onChange={v => update(`${k}_${l}`, v)} /></div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}

function FeaturesList({ value, onChange, placeholder }) {
    const list = value || [];
    return (
        <div className="space-y-2">
            {list.map((f, i) => (
                <div key={i} className="flex gap-2">
                    <Input
                        value={f}
                        onChange={e => {
                            const next = [...list];
                            next[i] = e.target.value;
                            onChange(next);
                        }}
                        className="bg-cafe-cream border-border rounded-xl text-sm"
                    />
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                        className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-10 w-10 shrink-0"
                    >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                </div>
            ))}
            <Button
                variant="outline"
                onClick={() => onChange([...list, ""])}
                className="rounded-full border-dashed border-cafe-olive/40 text-cafe-olive bg-transparent hover:bg-cafe-olive/10 h-9 px-4 text-xs"
            >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add bullet
            </Button>
        </div>
    );
}

function PackageCard({ pkg, onChange, onDelete, onMove, isFirst, isLast, isNew }) {
    const update = (k, v) => onChange({ ...pkg, [k]: v });
    return (
        <div
            data-testid={`pkg-card-${pkg.id}`}
            className={cn(
                "bg-cafe-latte/60 border rounded-3xl p-6 md:p-8",
                pkg.popular ? "border-cafe-terracotta" : "border-border",
                !pkg.active && "opacity-60",
            )}
        >
            <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">
                        Order: {pkg.sort_order}
                    </span>
                    <span
                        data-testid={`pkg-kind-${pkg.id}`}
                        className={cn(
                            "text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1",
                            (pkg.kind || "shoot") === "kit"
                                ? "bg-cafe-terracotta/15 text-cafe-terracotta border border-cafe-terracotta/40"
                                : pkg.kind === "addon"
                                    ? "bg-[#D4A574]/15 text-[#B88A55] border border-[#D4A574]/40"
                                    : "bg-cafe-olive/15 text-cafe-olive border border-cafe-olive/40",
                        )}
                    >
                        {(pkg.kind || "shoot") === "kit"
                            ? "AI Kit · digital"
                            : pkg.kind === "addon"
                                ? "Add-on · extras"
                                : "Shoot · on-site"}
                    </span>
                    {pkg.popular && (
                        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-cafe-terracotta text-cafe-cream inline-flex items-center gap-1">
                            <Star className="h-3 w-3" /> Most popular
                        </span>
                    )}
                    {!pkg.active && (
                        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-cafe-mocha text-cafe-cream">
                            Hidden
                        </span>
                    )}
                    {isNew && (
                        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-cafe-olive text-cafe-cream">
                            Unsaved
                        </span>
                    )}
                </div>
                <div className="flex gap-1">
                    <Button variant="outline" size="icon" disabled={isFirst}
                        onClick={() => onMove(-1)}
                        className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-9 w-9">
                        <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" disabled={isLast}
                        onClick={() => onMove(1)}
                        className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-cream h-9 w-9">
                        <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon"
                        onClick={onDelete}
                        className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-red-50 hover:text-red-700 h-9 w-9">
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* English */}
                <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">English</p>
                    <div>
                        <Label className="text-xs text-cafe-mocha">Name</Label>
                        <Input value={pkg.name_en} onChange={e => update("name_en", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">Description</Label>
                        <Textarea rows={2} value={pkg.description_en} onChange={e => update("description_en", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl text-sm" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">Unit (eg. / reel)</Label>
                        <Input value={pkg.unit_en} onChange={e => update("unit_en", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">Included (bullets)</Label>
                        <div className="mt-2">
                            <FeaturesList value={pkg.features_en} onChange={v => update("features_en", v)} />
                        </div>
                    </div>
                </div>

                {/* Chinese */}
                <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">中文</p>
                    <div>
                        <Label className="text-xs text-cafe-mocha">名称</Label>
                        <Input value={pkg.name_cn} onChange={e => update("name_cn", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">描述</Label>
                        <Textarea rows={2} value={pkg.description_cn} onChange={e => update("description_cn", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl text-sm" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">单位 (例：/ 条)</Label>
                        <Input value={pkg.unit_cn} onChange={e => update("unit_cn", e.target.value)}
                            className="mt-1 bg-cafe-cream border-border rounded-xl" />
                    </div>
                    <div>
                        <Label className="text-xs text-cafe-mocha">包含（项目）</Label>
                        <div className="mt-2">
                            <FeaturesList value={pkg.features_cn} onChange={v => update("features_cn", v)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Pricing & flags row */}
            <div className="mt-6 pt-6 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <Label className="text-xs text-cafe-mocha">Package type</Label>
                    <select
                        data-testid={`pkg-kind-select-${pkg.id}`}
                        value={pkg.kind || "shoot"}
                        onChange={e => update("kind", e.target.value)}
                        className="mt-1 w-full bg-cafe-cream border border-border rounded-xl px-3 h-10 text-sm"
                    >
                        <option value="shoot">Shoot · Kaily on-site (WhatsApp CTA)</option>
                        <option value="kit">AI Kit · digital (Gumroad CTA)</option>
                        <option value="addon">Add-on · attaches to a shoot (RM extra)</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs text-cafe-mocha">Price (RM, no symbol)</Label>
                    <Input value={pkg.price} onChange={e => update("price", e.target.value)}
                        className="mt-1 bg-cafe-cream border-border rounded-xl font-mono" placeholder="800" />
                </div>
                <div>
                    <Label className="text-xs text-cafe-mocha">Sort order</Label>
                    <Input type="number" value={pkg.sort_order} onChange={e => update("sort_order", parseInt(e.target.value) || 0)}
                        className="mt-1 bg-cafe-cream border-border rounded-xl font-mono" />
                </div>

                {(pkg.kind || "shoot") === "kit" && (
                    <div className="md:col-span-3">
                        <Label className="text-xs text-cafe-mocha">Gumroad URL <span className="text-cafe-mocha/60">· paste full checkout URL after creating product on gumroad.com</span></Label>
                        <Input
                            data-testid={`pkg-gumroad-${pkg.id}`}
                            value={pkg.gumroad_url || ""}
                            onChange={e => update("gumroad_url", e.target.value)}
                            placeholder="https://kaily.gumroad.com/l/your-product-slug"
                            className="mt-1 bg-cafe-cream border-border rounded-xl font-mono text-xs"
                        />
                        {!pkg.gumroad_url && (
                            <p className="text-[10px] text-cafe-terracotta mt-1.5 italic">
                                Empty Gumroad URL → public site shows "Notify me when live" instead of "Buy now".
                            </p>
                        )}
                    </div>
                )}

                {(pkg.kind || "shoot") === "kit" && (
                    <div className="md:col-span-3">
                        <Label className="text-xs text-cafe-mocha">
                            Gumroad product_id <span className="text-cafe-mocha/60">· webhook uses this to route orders. Open the Gumroad product page → copy the <code>product_id</code> from the URL or API.</span>
                        </Label>
                        <Input
                            data-testid={`pkg-gumroad-pid-${pkg.id}`}
                            value={pkg.gumroad_product_id || ""}
                            onChange={e => update("gumroad_product_id", e.target.value)}
                            placeholder="e.g. ABcdefGHi"
                            className="mt-1 bg-cafe-cream border-border rounded-xl font-mono text-xs"
                        />
                        {!pkg.gumroad_product_id && (
                            <p className="text-[10px] text-cafe-terracotta mt-1.5 italic">
                                Empty → webhook will fall back to product_name string matching (unreliable). Set this once per product.
                            </p>
                        )}
                    </div>
                )}

                <div>
                    <Label className="text-xs text-cafe-mocha">Early bird price (optional)</Label>
                    <Input value={pkg.early_bird_price} onChange={e => update("early_bird_price", e.target.value)}
                        className="mt-1 bg-cafe-cream border-border rounded-xl font-mono" placeholder="288" />
                </div>
                {pkg.early_bird_price && (
                    <>
                        <div>
                            <Label className="text-xs text-cafe-mocha">Early bird note (EN)</Label>
                            <Input value={pkg.early_bird_note_en} onChange={e => update("early_bird_note_en", e.target.value)}
                                className="mt-1 bg-cafe-cream border-border rounded-xl text-xs" placeholder="First 5 bookings only" />
                        </div>
                        <div>
                            <Label className="text-xs text-cafe-mocha">早鸟说明 (CN)</Label>
                            <Input value={pkg.early_bird_note_cn} onChange={e => update("early_bird_note_cn", e.target.value)}
                                className="mt-1 bg-cafe-cream border-border rounded-xl text-xs" placeholder="限首 5 位老板" />
                        </div>
                        <div></div>
                    </>
                )}
                <div className="flex items-center gap-3 mt-2">
                    <Switch checked={pkg.popular} onCheckedChange={v => update("popular", v)} data-testid={`pkg-popular-${pkg.id}`} />
                    <Label className="text-xs text-cafe-mocha cursor-pointer">Mark as Most Popular</Label>
                </div>
                <div className="flex items-center gap-3 mt-2">
                    <Switch checked={pkg.active} onCheckedChange={v => update("active", v)} data-testid={`pkg-active-${pkg.id}`} />
                    <Label className="text-xs text-cafe-mocha cursor-pointer">Active (visible on site)</Label>
                </div>
            </div>
            {(pkg.kind || "shoot") === "shoot" && <ScopeEditor pkg={pkg} update={update} />}
        </div>
    );
}

export default function PackagesTab() {
    const [packages, setPackages] = useState([]);
    const [originals, setOriginals] = useState({}); // id -> snapshot for diff
    const [newIds, setNewIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [savingAll, setSavingAll] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/packages/all`, { headers: studioHeaders() });
            const sorted = [...(data || [])].sort((a, b) => a.sort_order - b.sort_order);
            setPackages(sorted);
            const snap = {};
            sorted.forEach(p => { snap[p.id] = JSON.stringify(p); });
            setOriginals(snap);
            setNewIds(new Set());
        } catch (err) {
            toast.error("Failed to load packages");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updatePkg = (id, next) => {
        setPackages(prev => prev.map(p => p.id === id ? next : p));
    };

    const move = (id, dir) => {
        setPackages(prev => {
            const idx = prev.findIndex(p => p.id === id);
            if (idx === -1) return prev;
            const target = idx + dir;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[idx], next[target]] = [next[target], next[idx]];
            return next.map((p, i) => ({ ...p, sort_order: i + 1 }));
        });
    };

    const addNew = () => {
        const fresh = blankPackage();
        fresh.sort_order = packages.length + 1;
        setPackages(prev => [...prev, fresh]);
        setNewIds(prev => new Set([...prev, fresh.id]));
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this package?")) return;
        if (newIds.has(id)) {
            setPackages(prev => prev.filter(p => p.id !== id));
            setNewIds(prev => { const s = new Set(prev); s.delete(id); return s; });
            return;
        }
        try {
            await axios.delete(`${API}/packages/${id}`, { headers: studioHeaders() });
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const saveAll = async () => {
        setSavingAll(true);
        try {
            for (const p of packages) {
                if (newIds.has(p.id)) {
                    await axios.post(`${API}/packages`, p, { headers: studioHeaders() });
                } else if (originals[p.id] && originals[p.id] !== JSON.stringify(p)) {
                    await axios.put(`${API}/packages/${p.id}`, p, { headers: studioHeaders() });
                }
            }
            toast.success("All changes saved · live on site");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingAll(false);
        }
    };

    const dirty = packages.some(p => newIds.has(p.id) || originals[p.id] !== JSON.stringify(p));

    if (loading) return <div className="text-cafe-mocha py-8">Loading…</div>;

    return (
        <div data-testid="packages-tab">
            <TravelZonesEditor />
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <p className="text-cafe-mocha text-sm">{packages.length} packages · changes go live on <code>/pricing</code> instantly</p>
                <div className="flex flex-wrap gap-2">
                    <Button
                        onClick={async () => {
                            if (!window.confirm("Reseed will overwrite ALL pricing/description/scope fields of the default packages (Social Content + Brand Campaign + AI Kits) with the values currently in code. Custom packages and your gumroad_url + early_bird_price are PRESERVED. Continue?")) return;
                            try {
                                const { data } = await axios.post(`${API}/packages/reseed`, {}, { headers: studioHeaders() });
                                toast.success(`Reseed complete: ${data.updated} updated, ${data.created} created`);
                                load();
                            } catch (err) {
                                toast.error(err?.response?.data?.detail || "Reseed failed");
                            }
                        }}
                        variant="outline"
                        data-testid="reseed-pkg"
                        title="Force-overwrite the default packages from server.py code (use after a redeploy when prices/copy in code are newer than what's in this DB)"
                        className="rounded-full border-cafe-terracotta/40 bg-transparent text-cafe-terracotta hover:bg-cafe-terracotta/10 h-10 px-5 text-xs"
                    >
                        ↻ Reseed from code
                    </Button>
                    <Button onClick={addNew} variant="outline"
                        data-testid="add-pkg"
                        className="rounded-full border-cafe-espresso/20 bg-transparent hover:bg-cafe-latte h-10 px-5 text-xs">
                        <Plus className="h-4 w-4 mr-1.5" /> Add package
                    </Button>
                    <Button onClick={saveAll} disabled={!dirty || savingAll}
                        data-testid="save-all-pkg"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5 text-xs">
                        <Save className="h-4 w-4 mr-1.5" /> {savingAll ? "Saving…" : dirty ? "Save all changes" : "Saved"}
                    </Button>
                </div>
            </div>

            <div className="space-y-6">
                {packages.map((p, i) => (
                    <PackageCard
                        key={p.id}
                        pkg={p}
                        isNew={newIds.has(p.id)}
                        isFirst={i === 0}
                        isLast={i === packages.length - 1}
                        onChange={next => updatePkg(p.id, next)}
                        onMove={dir => move(p.id, dir)}
                        onDelete={() => remove(p.id)}
                    />
                ))}
            </div>
        </div>
    );
}
